import React, {Component} from 'react';
import { bindActionCreators } from 'redux';
import {connect} from 'react-redux';
import LineChart from '../../components/FeedingGuidelines/LineChart';
import * as actionTypes from './../../store/actions/feedingGuidelinesLineChartAction';
import * as moment from 'moment';
import fetchFeedingGuidelinesLineChartAction from './../../store/actions/fetch/fetchFeedingGuidelinesLineChartAction';
import Loader from './../../components/Loader/Loader'
import LineChartOptions from './LineChartOptions';
import FeedingGuidelinesLineChartType from './../../types/FeedingGuidelinesLineChartType';

import * as pdfOutputAction from './../../store/actions/pdfOutputAction';
import domtoimage from 'dom-to-image';
import OnImagesLoaded from 'react-on-images-loaded';

let chartOptions = LineChartOptions;
let chartData = {};

class FeedingGuidelinesLineChart extends Component {

  constructor(props) {
    super(props);
    const {fetchData, setChartData, output} = this.props;
    fetchData();

    const start = moment();
    const currentWeight = parseFloat(output.pet.weight.current);
    let targetWeight = parseFloat(output.pet.weight.goal);

    // If goal weight is not set, we use the ideal weight
    if (targetWeight == 0 || targetWeight == null || isNaN(targetWeight)) {
      targetWeight = ((100 * output.pet.weight.current) / ((output.pet.weight.bcs - 5) * 10 + 100));
    }

    const {minWeightReductionPoints, maxWeightReductionPoints} = this.getDataSets(currentWeight, targetWeight);
    const {dateFormat} = this.props.data;

    if (minWeightReductionPoints && maxWeightReductionPoints){
      chartData = {
        labels: this.getHorizontalLabels(start, minWeightReductionPoints.length, dateFormat),
        datasets: [{
          label: "minWeightReductionPoints",
          borderColor: "#808080",
          fill: false,
          data: minWeightReductionPoints,
        },{
          label: "maxWeightReductionPoints",
          borderColor: "#ea6b6a",
          fill: false,
          data: maxWeightReductionPoints,
        }]
      };

      chartOptions.scales.yAxes = [{
        ticks: this.getVerticalAxisRange(currentWeight, targetWeight),
      }];

      setChartData(chartData, chartOptions);
    }
  }

  /**
   * @function getHorizontalLabels
   * @memberof FeedingGuidelinesLineChart
   *
   * Calculates the labels for the horizontal axis by iterating the days between
   * the provided start and the end dates.
   *
   * @param {moment} start The start date to iterate
   * @param {int} weeks The number of weeks to iterate
   * @param {string} dateFormat The format of the date
   * @returns {array} An array of date strings
   */
  getHorizontalLabels(start, weeks, dateFormat) {
    const maxLabelsX = 12;
    let horizontalLabels = [];
    let weekCount = 0;
    let skipSecond = (weeks > maxLabelsX) ? true : false;

    while(weekCount < weeks) {
      let thisWeek = moment(start)
      thisWeek.add(weekCount * 7, 'days');

      /*
      // If out x axis is too large we skip every second label
      if (skipSecond && weekCount % 2 != 0){
        horizontalLabels.push("");
      } else {
        horizontalLabels.push(thisWeek.format('YYYY-MM-DD'));
      }
      */
      horizontalLabels.push(thisWeek.format('DD-MM-YYYY'));

      weekCount++;
    }

    return horizontalLabels;
  }

  /**
   * @function getDataSets
   * @memberof LineChart
   *
   * Calculates the datasets for the currentWeight to the targetWeight.
   * Returns an object containing 2 arrays of points. Each point in the dataset
   * represents the current weight after one week of weight loss at the 2 rates
   * (0.5% and 2.0%).
   *
   * @param {number} currentWeight
   * @param {number} targetWeight
   * @returns {object}
   */
  getDataSets(currentWeight, targetWeight){
    // Calculate the amount of weeks to reach the target weight at the two  rates
    let minWeightAtTime = currentWeight;
    let maxWeightAtTime = currentWeight;
    let maxWeightReductionPoints = []
    let minWeightReductionPoints = []

    maxWeightReductionPoints.push(parseFloat(currentWeight));
    minWeightReductionPoints.push(parseFloat(currentWeight));

    // Is the graph increasing or decreasing
    if (currentWeight > targetWeight){
      while(minWeightAtTime > targetWeight) {
        minWeightAtTime = minWeightAtTime - ((minWeightAtTime / 100) * 0.5);
        minWeightReductionPoints.push(parseFloat(minWeightAtTime));
      }

      while(maxWeightAtTime > targetWeight) {
        maxWeightAtTime = maxWeightAtTime - ((maxWeightAtTime / 100) * 2.0);
        maxWeightReductionPoints.push(parseFloat(maxWeightAtTime));
      }
    } else {
      while(minWeightAtTime < targetWeight) {
        minWeightAtTime = minWeightAtTime + ((minWeightAtTime / 100) * 0.5);
        minWeightReductionPoints.push(parseFloat(minWeightAtTime));
      }

      while(maxWeightAtTime < targetWeight) {
        maxWeightAtTime = maxWeightAtTime + ((maxWeightAtTime / 100) * 2.0);
        maxWeightReductionPoints.push(parseFloat(maxWeightAtTime));
      }
    }

    // Pad the maxWeightReductionPoints array to be the same length as the minWeightReductionPoints
    let difference = minWeightReductionPoints.length - maxWeightReductionPoints.length;
    const lastValue = maxWeightReductionPoints[maxWeightReductionPoints.length - 1]
    while(difference > 0){
      maxWeightReductionPoints.push(lastValue);
      difference--;
    }

    return {
      minWeightReductionPoints: minWeightReductionPoints,
      maxWeightReductionPoints: maxWeightReductionPoints,
    }
  }

  /**
   * @function getVerticalAxisRange
   * @memberof LineChart
   *
   * Calculates the range and step size of the vertical axis based on the
   * currentWeight.
   *
   * @param {number} currentWeight
   * @param {number} targetWeight
   * @returns {object}
   */
  getVerticalAxisRange(currentWeight, targetWeight) {
    // Determine the range we're rounding to based on the currentWeight
    let roundingModifier = 10;
    if (currentWeight <= 10 || currentWeight === targetWeight) {
      roundingModifier = 1;
    } else if (currentWeight <= 25) {
      roundingModifier = 5;
    }

    let offset = currentWeight * 0.25;
    offset = Math.ceil(offset / roundingModifier) * roundingModifier;

    if (currentWeight > targetWeight){
      return {
        min: targetWeight,
        max: currentWeight,
        stepSize: Math.round(roundingModifier / 5),
      };
    }

    return {
      min: currentWeight,
      max: targetWeight,
      stepSize: Math.round(roundingModifier / 5),
    };
  }

  doScreenShot() {
    const {screenshots}  = this.props;
    const el = document.getElementById('fc-screenshot-feeding-guidelines-line-chart');
    const {setScreenShot, onScreenshotError} = this.props;
    if (el){
      domtoimage.toJpeg(el, { quality: 0.7 })
        .then(function (dataUrl) {
          if (screenshots && screenshots['fc-screenshot-feeding-guidelines-line-chart'] !== dataUrl){
            setScreenShot(dataUrl);
          }
        })
        .catch(function () {
          onScreenshotError();
        });
    } else {
      onScreenshotError();
    }
  }

  render() {
    const {data, pending, ready, chartData, chartOptions} = this.props;

    if(pending || !ready || !chartData) return (
      <Loader/>
    );

    chartOptions.animation = {
      onComplete: () =>  {
        this.doScreenShot();
      }
    };

    return (
      <OnImagesLoaded
        onLoaded={this.doScreenShot.bind(this)}
        onTimeout={this.doScreenShot.bind(this)}
        timeout={15000}>

        <LineChart
          data={data}
          chartData={chartData}
          chartOptions={chartOptions}
        />

      </OnImagesLoaded>
    );
  }
}

const mapStateToProps = state => {
  return {
    data: state.feedingGuidelinesLineChartReducer.data,
    pending: state.feedingGuidelinesLineChartReducer.pending,
    ready: state.feedingGuidelinesLineChartReducer.ready,
    error: state.feedingGuidelinesLineChartReducer.error,
    chartData: state.feedingGuidelinesLineChartReducer.chartData,
    chartOptions: state.feedingGuidelinesLineChartReducer.chartOptions,
    screenshots: state.pdfOutputReducer.images,
  }
};

const mapDispatchToProps = dispatch => {
  return {
    ...bindActionCreators(
      {
        fetchData: fetchFeedingGuidelinesLineChartAction
      },
      dispatch,
    ),
    defaultValues: () => dispatch({
      type: actionTypes.DEFAULT,
    }),
    setChartData: (chartData, chartOptions) => dispatch({
      type: actionTypes.SET_CHART_DATA,
      chartData: chartData,
      chartOptions: chartOptions,
    }),
    setScreenShot: (data) => dispatch({
      type: pdfOutputAction.ADD,
      key: 'fc-screenshot-feeding-guidelines-line-chart',
      data: data,
    }),
    onScreenshotError: () => dispatch({
      type: pdfOutputAction.SCREENSHOT_ERROR,
    }),
  }
};

FeedingGuidelinesLineChart.propTypes = FeedingGuidelinesLineChartType;

export default connect(mapStateToProps, mapDispatchToProps)(FeedingGuidelinesLineChart);
